import { Component, Injector, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { Borrower } from '../../../models/borrower-model';
import { ErrorMessage } from '../../../models/error-message.model';
import { MortgageApplication } from '../../../models/mortgage-app.model';
import { MortgageApplicationService } from '../../../services/mortgage-application.service';
import { WizardStepComponentBase } from '../wizard-step-base.component';
import { NumberOfBorrowersStep } from '../../../models/wizard/number-of-borrowers-step.model';
import { activeOnly } from '../../../utils/entity-state';

@Component({
    selector: 'number-of-borrowers',
    templateUrl: 'number-of-borrowers.component.html',
    standalone: false
})
export class NumberOfBorrowersComponent extends WizardStepComponentBase<NumberOfBorrowersStep> implements OnInit {

  @ViewChild('numberOfBorrowersForm') numberOfBorrowersForm: NgForm | undefined;

  private _mortgageApplication: MortgageApplication;

  public numberOfCoBorrowers: number | undefined = 0;

  public hasCoBorrowers: boolean | null = null;

  onYesSelected = () => {
    this.numberOfCoBorrowers = 1;
    this.hasCoBorrowers = true;
    this.step.requiresNextButton = true;
  }

  onNumberOfCoBorrowersChanged = () => {
    if (this.numberOfCoBorrowers) {
      if (this.numberOfCoBorrowers < 0) {
        this.numberOfCoBorrowers = 0;
      }
    }
  }

  onNoSelected = () => {
    this.hasCoBorrowers = false;
    this.numberOfCoBorrowers = 0;
    this.onNextClicked();
  }

  constructor(private readonly _injector: Injector, private _mortgageApplicationService: MortgageApplicationService) {
    super(_injector);
    this._mortgageApplication = this._mortgageApplicationService.mortgageApplication;
  }

  // Everyone on the loan except the borrower who is signed in. Their row is not always first in
  // mortgage.borrowers (the loan officer may have created another borrower before them), so the
  // co-borrowers this step adds and removes have to be identified by who they are, never by index.
  private get coBorrowers(): Borrower[] {
    const currentBorrower = this.wizardFlowService.context.currentBorrower;
    const borrowers = this._mortgageApplication.borrowers;
    const isCurrentBorrower = (borrower: Borrower) => borrower === currentBorrower
      || (!!currentBorrower?.contactId && borrower.contactId === currentBorrower.contactId);
    if (currentBorrower && borrowers.some(isCurrentBorrower)) {
      return borrowers.filter(borrower => !isCurrentBorrower(borrower));
    }
    // No signed-in borrower on the loan yet (the register flow, before anything is saved).
    return borrowers.slice(1);
  }

  ngOnInit() {
    this.step.requiresNextButton = false;
    this.numberOfCoBorrowers = this.coBorrowers.length;
    if (this.numberOfCoBorrowers > 0) {
      this.hasCoBorrowers = true;
      this.step.requiresNextButton = true;
    } else {
      if (this.wizardFlowService.context.hasMultipleBorrowers === false) {
        this.hasCoBorrowers = false;
        this.step.requiresNextButton = true;
      }
    }
    if (!this.wizardFlowService.canManageCoBorrowers) {
      this.step.requiresNextButton = true;
    }
  }

  onNextClicked() {
    if (!this.wizardFlowService.canManageCoBorrowers) {
      super.onNextClicked();
      return;
    }
    this.numberOfBorrowersForm?.form.markAllAsTouched();
    if (this.numberOfBorrowersForm?.form.valid) {
      const coBorrowers = this.coBorrowers;
      const existingNumberOfCoBorrowers = coBorrowers.length;
      if (this.numberOfCoBorrowers == null) {
        super.onNextClicked();
        return;
      }
      this.wizardFlowService.context.hasMultipleBorrowers = this.numberOfCoBorrowers > 0;
      if (this.numberOfCoBorrowers > existingNumberOfCoBorrowers) {
        for (let i = 1; i <= this.numberOfCoBorrowers - existingNumberOfCoBorrowers; i++) {
          const coBorrower = new Borrower();
          coBorrower.borrowerId = 0 - i;
          this._mortgageApplication.borrowers.push(coBorrower);
        }
        super.onNextClicked();
      } else {
        const numberOfBorrowersToRemove = existingNumberOfCoBorrowers - this.numberOfCoBorrowers;
        const borrowersToRemove = coBorrowers.slice(existingNumberOfCoBorrowers - numberOfBorrowersToRemove);
        const apiCalls: any[] = [];

        for (const borrowerToRemove of borrowersToRemove) {
          if (borrowerToRemove.contactId && borrowerToRemove.contactId > 0) {
            const apiCallToRemoveBorrower = this._mortgageApplicationService.removeBorrower(this._mortgageApplication.applicationId,
              borrowerToRemove.contactId);
            apiCalls.push(apiCallToRemoveBorrower);
          }
          const index = this._mortgageApplication.borrowers.indexOf(borrowerToRemove);
          if (index >= 0) {
            this._mortgageApplication.borrowers.splice(index, 1);
          }
          activeOnly(this._mortgageApplication.realEstateOwned).forEach(reo => {
            const ownerIndex = reo.owningBorrowerIds.findIndex(id => id == borrowerToRemove.borrowerId);
            if (ownerIndex >= 0) {
              reo.owningBorrowerIds.splice(ownerIndex, 1);
            }
          })
        }

        if (apiCalls.length) {
          this.startSpinner();
          forkJoin(apiCalls).subscribe(() => {
            super.onNextClicked();
          }, error => {
            this.showError(new ErrorMessage("Error", error.message));
          }).add(() => {
            this.stopSpinner();
          })
        } else {
          super.onNextClicked();
        }
      }
    }
  }
}
